<script setup lang="ts">
import {computed, ref} from "vue";
import {Sparkles, Stethoscope} from "@lucide/vue";
import {Input} from "@/core/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import {BreastFeedingStatus, HepaticStatus} from "@/customer/interfaces/DeclaredInformation";
import {PatientProfile, PatientProfileView} from "@/medication-checkup/interfaces/patient-profile";
import MedicationCheckupVidalSearchChips from "@/medication-checkup/components/MedicationCheckupVidalSearchChips.vue";
import MedicationCheckupBodyMassIndexGauge from "@/medication-checkup/components/MedicationCheckupBodyMassIndexGauge.vue";
import {CARD_SHEET} from "@/medication-checkup/services/SceneClasses";

/**
 * The card of the patient profile: measures, renal and hepatic function,
 * allergies and pathologies, pregnancy and breastfeeding when they apply.
 *
 * Entering values is local to the scene: the card shows the draft it is
 * handed while live — pre-filled from the patient record by the handler —,
 * the profile the server recorded once the block is completed, read in clear.
 * The body mass index is computed, never entered.
 */

const props = defineProps<{
    view: PatientProfileView;
    frozen?: boolean;
    /**
     * The profile as entered so far, while the block is live.
     */
    draft?: PatientProfile;
}>();

const emit = defineEmits<{
    'update:draft': [draft: PatientProfile];
}>();

const HEPATIC_LABELS: Record<HepaticStatus, string> = {
    [HepaticStatus.NONE]: 'Aucune',
    [HepaticStatus.MODERATE]: 'Modérée',
    [HepaticStatus.SEVERE]: 'Sévère',
};

const BREAST_FEEDING_LABELS: Record<BreastFeedingStatus, string> = {
    [BreastFeedingStatus.NONE]: 'Aucun',
    [BreastFeedingStatus.LESS_THAN_ONE_MONTH]: "Moins d'1 mois",
    [BreastFeedingStatus.MORE_THAN_ONE_MONTH]: "Plus d'1 mois",
    [BreastFeedingStatus.ALL]: 'Allaitement en cours',
};

const GENDER_LABELS = {male: 'homme', female: 'femme'};

/**
 * Whether the folded pregnancy section was opened by the pharmacist.
 */
const fertilityOpened = ref(false);

/**
 * What the card reads: the draft while live, the recorded profile once
 * frozen.
 */
const profile = computed<PatientProfile>(() => props.frozen ? props.view.profile : props.draft ?? props.view.profile);

const patient = computed(() => props.view.patient);

/**
 * The identity beside the title: name, age and gender, whichever the record
 * carries.
 */
const identity = computed(() => {
    return [
        patient.value.name,
        patient.value.age !== null ? `${patient.value.age} ans` : null,
        patient.value.gender ? GENDER_LABELS[patient.value.gender] : null,
    ].filter(Boolean).join(' · ');
});

/**
 * The body mass index from the weight and height entered, one decimal, as
 * the pharmacist reads it — nothing while either is missing.
 */
const bodyMassIndex = computed(() => {
    const {weight, height} = profile.value;

    if (!weight || !height) {
        return null;
    }

    return weight / Math.pow(height / 100, 2);
});

const bodyMassIndexLabel = computed(() => bodyMassIndex.value?.toFixed(1).replace('.', ',') ?? '—');

const showsFertility = computed(() => {
    return props.view.fertility === 'expanded' || (props.view.fertility === 'collapsed' && fertilityOpened.value);
});

/**
 * Whether the frozen pregnancy section has something to read.
 */
const fertilityFilled = computed(() => {
    return profile.value.amenorrhoea !== null
        || profile.value.breast_feeding !== BreastFeedingStatus.NONE
        || profile.value.breast_feeding_date !== null;
});

const measure = (value: number | null, unit: string): string => value === null ? '—' : `${value} ${unit}`;

const set = <K extends keyof PatientProfile>(key: K, value: PatientProfile[K]) => {
    emit('update:draft', {...profile.value, [key]: value});
};

const setInteger = (key: 'weight' | 'height' | 'dfg' | 'amenorrhoea', typed: string | number) => {
    const value = String(typed).trim();

    set(key, value === '' ? null : Math.max(0, parseInt(value, 10)) || null);
};

const setDate = (typed: string | number) => {
    set('breast_feeding_date', typed === '' ? null : String(typed));
};

/**
 * The shadcn field tuned to the card: white ground, primary ring.
 */
const fieldClass = 'bg-white focus-visible:ring-primary';
</script>

<template>
  <div
    class="max-w-[640px] rounded-xl"
    :class="[CARD_SHEET, frozen ? 'overflow-hidden' : 'overflow-visible']"
  >
    <div class="flex items-center gap-2 px-4 pt-3" :class="frozen ? 'pb-3' : 'pb-1.5'">
      <Stethoscope class="w-[18px] h-[18px] text-primary" />
      <span class="text-sm font-semibold text-primary">Profil patient</span>
      <span class="text-sm text-muted-foreground">— {{ identity }}</span>
    </div>

    <div v-if="!frozen" class="flex items-center gap-1.5 px-4 pb-2.5 text-xs text-muted-foreground">
      <Sparkles class="w-3 h-3 shrink-0 text-primary" />
      <span>Pré-rempli d'après le dossier patient · vérifiez chaque valeur avec {{ patient.name }} et complétez ce qui manque</span>
    </div>

    <div class="flex flex-col gap-3 px-4 py-3 border-t border-gray-light">
      <span v-if="!frozen" class="text-xs font-bold uppercase tracking-wider text-muted-foreground">Mesures</span>

      <div class="grid grid-cols-3 gap-4">
        <template v-if="frozen">
          <div class="flex flex-col gap-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Poids</span>
            <span class="text-sm font-semibold">{{ measure(profile.weight, 'kg') }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Taille</span>
            <span class="text-sm font-semibold">{{ measure(profile.height, 'cm') }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">IMC</span>
            <span class="flex items-center gap-2.5">
              <span class="text-sm font-semibold">{{ bodyMassIndexLabel }}</span>
              <MedicationCheckupBodyMassIndexGauge v-if="bodyMassIndex !== null" :value="bodyMassIndex" compact />
            </span>
          </div>
        </template>

        <template v-else>
          <label class="flex flex-col gap-1.5 min-w-0">
            <span class="text-xs font-semibold leading-none">Poids (kg)</span>
            <Input
              type="number"
              min="1"
              :model-value="profile.weight ?? ''"
              placeholder="À renseigner"
              :class="fieldClass"
              @update:model-value="setInteger('weight', $event)"
            />
          </label>
          <label class="flex flex-col gap-1.5 min-w-0">
            <span class="text-xs font-semibold leading-none">Taille (cm)</span>
            <Input
              type="number"
              min="1"
              :model-value="profile.height ?? ''"
              placeholder="À renseigner"
              :class="fieldClass"
              @update:model-value="setInteger('height', $event)"
            />
          </label>
          <div class="flex flex-col gap-1.5 min-w-0">
            <span class="text-xs font-semibold leading-none">IMC</span>
            <div class="flex items-center h-9 w-full px-3 rounded-lg border border-transparent bg-gray-light text-sm">
              <span>{{ bodyMassIndexLabel }}</span>
              <span class="ml-auto text-[10px] text-muted-foreground">calculé</span>
            </div>
            <MedicationCheckupBodyMassIndexGauge v-if="bodyMassIndex !== null" :value="bodyMassIndex" />
          </div>
        </template>
      </div>
    </div>

    <div class="flex flex-col gap-3 px-4 py-3 border-t border-gray-light">
      <span v-if="!frozen" class="text-xs font-bold uppercase tracking-wider text-muted-foreground">Fonction rénale et hépatique</span>

      <div class="grid grid-cols-2 gap-4">
        <template v-if="frozen">
          <div class="flex flex-col gap-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">DFG</span>
            <span class="text-sm font-semibold">{{ measure(profile.dfg, 'ml/min') }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Insuffisance hépatique</span>
            <span class="text-sm font-semibold">{{ HEPATIC_LABELS[profile.hepatic] }}</span>
          </div>
        </template>

        <template v-else>
          <label class="flex flex-col gap-1.5 min-w-0">
            <span class="text-xs font-semibold leading-none">DFG (ml/min)</span>
            <Input
              type="number"
              min="0"
              :model-value="profile.dfg ?? ''"
              placeholder="À renseigner"
              :class="fieldClass"
              @update:model-value="setInteger('dfg', $event)"
            />
            <span class="text-[10px] italic text-muted-foreground">Clairance de la créatinine, dernier bilan connu</span>
          </label>
          <label class="flex flex-col gap-1.5 min-w-0">
            <span class="text-xs font-semibold leading-none">Insuffisance hépatique</span>
            <Select :model-value="profile.hepatic" @update:model-value="set('hepatic', $event as PatientProfile['hepatic'])">
              <SelectTrigger class="rounded-lg" :class="fieldClass">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="(label, status) in HEPATIC_LABELS"
                  :key="status"
                  :value="status"
                >
                  {{ label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </label>
        </template>
      </div>
    </div>

    <div class="flex flex-col gap-2 px-4 py-3 border-t border-gray-light">
      <span class="text-xs font-bold uppercase tracking-wider text-muted-foreground">Allergies</span>
      <MedicationCheckupVidalSearchChips
        kind="allergy"
        :entries="profile.allergies"
        :frozen="frozen"
        @update:entries="set('allergies', $event)"
      />
    </div>

    <div class="flex flex-col gap-2 px-4 py-3 border-t border-gray-light">
      <span class="text-xs font-bold uppercase tracking-wider text-muted-foreground">Pathologies</span>
      <MedicationCheckupVidalSearchChips
        kind="pathology"
        :entries="profile.pathologies"
        :frozen="frozen"
        @update:entries="set('pathologies', $event)"
      />
    </div>

    <div
      v-if="view.fertility === 'not_applicable'"
      class="flex items-center gap-2 px-4 py-3 border-t border-gray-light bg-gray-light rounded-b-xl text-sm text-muted-foreground"
    >
      <span class="font-semibold">Grossesse et allaitement</span>
      <span>— non applicable pour {{ patient.name }}</span>
    </div>

    <div
      v-else-if="!showsFertility && !frozen"
      class="flex items-center gap-2 px-4 py-3 border-t border-gray-light bg-gray-light rounded-b-xl text-sm text-muted-foreground"
    >
      <span class="font-semibold">Grossesse et allaitement</span>
      <button class="ml-auto text-sm font-semibold text-primary hover:underline" @click="fertilityOpened = true">
        Renseigner
      </button>
    </div>

    <div
      v-else-if="frozen && !fertilityFilled"
      class="flex items-center gap-2 px-4 py-3 border-t border-gray-light bg-gray-light text-sm text-muted-foreground"
    >
      <span class="font-semibold">Grossesse et allaitement</span>
      <span>— non renseigné</span>
    </div>

    <div v-else class="flex flex-col gap-3 px-4 py-3 border-t border-gray-light" :class="{'rounded-b-xl': !frozen}">
      <span v-if="!frozen" class="text-xs font-bold uppercase tracking-wider text-muted-foreground">Grossesse et allaitement</span>

      <div class="grid grid-cols-3 gap-4">
        <template v-if="frozen">
          <div class="flex flex-col gap-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Semaines d'aménorrhée</span>
            <span class="text-sm font-semibold">{{ measure(profile.amenorrhoea, 'semaines') }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Allaitement</span>
            <span class="text-sm font-semibold">{{ BREAST_FEEDING_LABELS[profile.breast_feeding] }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Début de l'allaitement</span>
            <span class="text-sm font-semibold">{{ profile.breast_feeding_date ?? '—' }}</span>
          </div>
        </template>

        <template v-else>
          <label class="flex flex-col gap-1.5 min-w-0">
            <span class="text-xs font-semibold leading-none">Semaines d'aménorrhée</span>
            <Input
              type="number"
              min="0"
              :model-value="profile.amenorrhoea ?? ''"
              placeholder="À renseigner"
              :class="fieldClass"
              @update:model-value="setInteger('amenorrhoea', $event)"
            />
          </label>
          <label class="flex flex-col gap-1.5 min-w-0">
            <span class="text-xs font-semibold leading-none">Allaitement</span>
            <Select :model-value="profile.breast_feeding" @update:model-value="set('breast_feeding', $event as PatientProfile['breast_feeding'])">
              <SelectTrigger class="rounded-lg" :class="fieldClass">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="(label, status) in BREAST_FEEDING_LABELS"
                  :key="status"
                  :value="status"
                >
                  {{ label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label class="flex flex-col gap-1.5 min-w-0">
            <span class="text-xs font-semibold leading-none">Début de l'allaitement</span>
            <Input
              type="date"
              :model-value="profile.breast_feeding_date ?? ''"
              :class="fieldClass"
              @update:model-value="setDate($event)"
            />
          </label>
        </template>
      </div>
    </div>
  </div>
</template>
